import React, { useEffect, useMemo, useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  Button,
  Dialog,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader
} from '@clientbase/clientbase-library'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Box,
  CircularProgress,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography
} from '@mui/material'
import { DataService } from 'api/DataService'
import { usePermissionsContext } from 'context/PermissionsContext'
import {
  getActiveMerchantUuid,
  registerRecordMerchant
} from 'lib/merchant-group'
import { AutoCompleteBillingsParams } from 'models/Billings/AutoComplete'
import { Products } from 'models/Plans'
import { BillingRule } from 'services/BillingRule'
import {
  ChargeBodyModalForm,
  ChargeRecurrenceBodyModalForm
} from 'templates/Charge/interface'
import { sharedChargeSchema } from 'templates/Charge/sharedCharge.schema'
import {
  formatSharedChargeData,
  getCreditCardPaymentOptionsText,
  sharedInitialFormValues,
  setSharedFormData
} from 'templates/Charge/sharedCharge.utils'

import CompanySelector, {
  CompanySessionProvider,
  useFormCompany,
  useInCompanyForm,
  useSessionCookies
} from 'components/CompanySelector'
import CreateGate from 'components/CreateGate'
import IconContainer from 'components/IconContainer/IconContainer'
import { PaymentsTypeRef } from 'components/PaymentsType'
import SharedChargeFields from 'components/SharedChargeFields/SharedChargeFields'
import TagsPopover from 'components/TagsPopover'

import { amountRange, isMobileViewport, track } from 'utils/tracking'
import { toastFirstError } from 'utils/yupResolverWithToast'

import {
  buildNfseActionPayload,
  getChangedSensitiveFields,
  getNfseEditContext,
  mapChargeToFormValues,
  mapRecurrenceToFormValues,
  NfseDialogMode,
  resolveNfseDialogMode
} from './BodyModalNewChargeOrRecurrence.edit.utils'
import { checkDescriptionVariant } from './BodyModalNewChargeOrRecurrence.utils'
import NfseReissueFields, {
  NfseEmitChoice
} from './Components/NfseReissueFields'
import NotifyCustomerFields, {
  NotifyChoice
} from './Components/NotifyCustomerFields'
import PayersAutocomplete from './Components/PayersAutocomplete'
import { parentSchema } from './parentSchema'

type HandleOpenDetailChargeOrRecurrence = {
  uuid: string
  totalCycles: number
}

export interface ChargeBodyModalClientProps {
  onClose: (refresh?: boolean) => void
  handleCloseModal: () => void
  revalidateTable?: () => void
  nameCustomer?: string
  handleOpenDetailChargeOrRecurrence?: (
    props: HandleOpenDetailChargeOrRecurrence
  ) => void
  isCharge?: boolean
  onSubmit?: (value: ChargeRecurrenceBodyModalForm) => void
  defaultValues?: Partial<ChargeRecurrenceBodyModalForm>
  plan?: Products
  // Edit mode props
  isEdit?: boolean
  isDuplicate?: boolean
  editType?: 'charge' | 'recurrence'
  editData?: any // InvoiceParams or RecurrencesParams
  uuid?: string
}

export type SelectCreditCards = {
  label: string
  value: string
  requiresReregistration?: boolean
}

type DialogProps = {
  isOpen: boolean
  description: string
  title: string
  handleClose: () => void
  action: () => void
  actionLabel: string
}

// Valores específicos deste formulário (não compartilhados)
const parentFormValues = {
  customer: { label: '', value: '' },
  group: { label: '', value: '' },
  customerUuid: '',
  groupUuid: '',
  recieverType: 'customer'
} as ChargeBodyModalForm & {
  payments: string[]
  recieverType: 'customer' | 'group' | undefined
  group?: AutoCompleteBillingsParams | undefined
  groupUuid?: string
  frequency?: number
}

type BodyModalNewChargeOrRecurrenceFormProps = ChargeBodyModalClientProps & {
  // SC-10999: seletor de empresa (só na visão multi-empresas)
  companySelector?: React.ReactNode
}

function BodyModalNewChargeOrRecurrenceForm({
  companySelector,
  onClose,
  revalidateTable,
  nameCustomer,
  handleOpenDetailChargeOrRecurrence,
  isCharge: initialIsCharge = true,
  onSubmit,
  defaultValues,
  plan: planDefault,
  // Edit mode props
  isEdit = false,
  isDuplicate = false,
  editType = 'charge',
  editData,
  uuid
}: BodyModalNewChargeOrRecurrenceFormProps) {
  const inCompanyForm = useInCompanyForm()
  const [groupLength, setGroupLength] = useState<number>(0)
  const [isCharge, setIsCharge] = useState(initialIsCharge)
  const [isUnique, setIsUnique] = useState(isCharge ? true : false)
  const paymentsTypeRef = useRef<PaymentsTypeRef>(null)
  const scrollToCreditCardFnRef = useRef<(() => void) | null>(null)
  // Guarda o dataForm do save da cobrança emitida para o botão do dialog chamar
  // o handleSubmitForm ATUAL (e não um closure preso ao estado de nota antigo).
  const pendingChargeFormRef = useRef<ChargeRecurrenceBodyModalForm | null>(
    null
  )

  const finalSchema = parentSchema.concat(sharedChargeSchema)

  // Dynamic resolver that updates with current state
  const dynamicResolver = useMemo(() => {
    return yupResolver(finalSchema, {
      context: {
        isCharge: isCharge,
        isUnique: isUnique,
        isProductOrLink: false,
        isLink: false
      }
    })
  }, [isCharge, isUnique, finalSchema])

  // SC-10999: na visão multi-empresas os padrões vêm da empresa escolhida
  const {
    ['nextauth.days_to_issue_billing']: days_to_issue_billing,
    ['nextauth.issue_billing_type']: issue_billing_type,
    ['nextauth.limit_days_for_payment']: limit_days_for_payment,
    ['nextauth.default_days_to_report']: default_days_to_report
  } = useSessionCookies()
  const cookieIssueBillingType =
    (issue_billing_type as 'days_before_due_date' | 'specific_day') ||
    'days_before_due_date'
  const {
    handleSubmit,
    control,
    watch,
    register,
    reset,
    setValue,
    setError,
    trigger,
    formState: { errors },
    clearErrors
  } = useForm<ChargeRecurrenceBodyModalForm>({
    mode: 'onChange',
    defaultValues: {
      ...sharedInitialFormValues,
      // O padrão de sharedInitialFormValues é lido do cookie na carga do
      // módulo; na visão multi-empresas vale o da empresa do formulário
      // (SC-10999).
      ...(inCompanyForm &&
        !!limit_days_for_payment && {
          expirationField: limit_days_for_payment
        }),
      ...(inCompanyForm &&
        !!default_days_to_report && {
          daysToReport: default_days_to_report
        }),
      ...parentFormValues,
      ...defaultValues,
      issueDays: Number(days_to_issue_billing),
      issueBillingType: cookieIssueBillingType,
      hasIssueDays: Number(days_to_issue_billing) > 0,
      ...(cookieIssueBillingType === 'specific_day' && {
        specificDayOfMonth: Number(days_to_issue_billing) || 1
      })
    },
    resolver: dynamicResolver
  })

  const [loading, setLoading] = useState(false)

  const { payments } = watch()

  const [dialog, setDialog] = useState<DialogProps>({
    isOpen: false,
    description: '',
    title: '',
    handleClose: () => null,
    action: () => null,
    actionLabel: ''
  })

  // Notification (update billing) dialog state
  // Toda edição (cobrança OU recorrência) usa o dialog unificado "Confirmar
  // alterações" (gate `isEdit`). `isIssuedChargeEdit` é o subconjunto cobrança
  // já emitida ao cliente — só ele habilita os steps de notificação (reenvio) e
  // de nota fiscal. Pendente/recorrência ficam só com métrica (+ reconciliação
  // de cobranças dirty, no caso da recorrência) + confirmação.
  const isIssuedChargeEdit = !!(isEdit && isCharge && editData?.issued)
  const [isIssuedDialog, setIsIssuedDialog] = useState(false)
  const [notifyChoice, setNotifyChoice] = useState<NotifyChoice>('silent')
  const [notifyChannels, setNotifyChannels] = useState<{
    email: boolean
    whatsapp: boolean
  }>({ email: false, whatsapp: false })
  // Tarifa ausente = canal sem cobrança (coexistence ativo no WhatsApp)
  const [notifyFees, setNotifyFees] = useState<{
    email?: number
    whatsapp?: number
  }>({ email: 0, whatsapp: 0 })
  const [notifyEnabled, setNotifyEnabled] = useState<{
    email: boolean
    whatsapp: boolean
  }>({ email: false, whatsapp: false })
  const [notifyFeesLoaded, setNotifyFeesLoaded] = useState(false)
  const [changedSensitiveFields, setChangedSensitiveFields] = useState<
    string[]
  >([])

  // Estado das opções de nota fiscal no dialog de edição (reemitir/empilhar/do-zero).
  const { hasPermission, hasAnyPermission } = usePermissionsContext()
  const nfsePerms = {
    canCancel: hasPermission('nfses:cancel'),
    canManage: hasPermission('nfses:manage'),
    canEmit: hasAnyPermission(['billings:generate_nfse', 'nfses:manage'])
  }
  const [nfseMode, setNfseMode] = useState<NfseDialogMode>('none')
  // Passo da nota: dois toggles independentes (reemitir / cancelar a antiga).
  const [nfseReemit, setNfseReemit] = useState(true)
  const [nfseCancelOld, setNfseCancelOld] = useState(false)
  const [nfseEmitChoice, setNfseEmitChoice] = useState<NfseEmitChoice>('now')
  const [nfseJustificativa, setNfseJustificativa] = useState(
    'Cancelamento por reemissão com dados atualizados'
  )
  const [nfseConfirmed, setNfseConfirmed] = useState<{
    uuid: string
    nfse_number: string | null
  } | null>(null)

  // Dialog de edição de cobrança emitida unificado em passos (Variante 8):
  // passo 1 = notificação; passo 2 = nota fiscal (quando defasada). A métrica
  // (MRR/AVULSA) não é mais perguntada aqui: é decidida no plano ou no item da
  // composição (SC-11024), e a tag da recorrência/cobrança não entra no MRR.
  const [wizardStep, setWizardStep] = useState<1 | 2>(1)
  // Edição de recorrência com cobranças pendentes editadas à mão (dirty):
  // false = manter as edições manuais (default, menos destrutivo);
  // true = aplicar as alterações da recorrência também nelas (force_dirty).
  const [applyToDirty, setApplyToDirty] = useState(false)

  const showNotifySection =
    isIssuedChargeEdit && (notifyEnabled.email || notifyEnabled.whatsapp)
  const showNfseStep = nfseMode !== 'none'
  // Só recorrência tem cobranças pendentes editadas à mão para reconciliar.
  const dirtyPendingCount =
    isEdit && !isCharge ? editData?.dirtyPendingBillingsCount ?? 0 : 0
  const showDirtyChoice = dirtyPendingCount > 0
  const hasStep1 = showNotifySection || showDirtyChoice
  const isWizard = hasStep1 && showNfseStep
  const onStep1 = isWizard ? wizardStep === 1 : hasStep1
  const onStep2 = isWizard ? wizardStep === 2 : showNfseStep && !hasStep1

  const loadNotifyFees = async () => {
    if (notifyFeesLoaded) return notifyEnabled
    try {
      const resp = await BillingRule.getPriceNotifications()
      const record = resp?.record
      if (record) {
        const enabled = {
          email: !!record.email_enabled,
          whatsapp: !!record.whatsapp_enabled
        }
        setNotifyFees({
          email: Number(record.email_fee) || 0,
          whatsapp: record.whatsapp_coexistence_active
            ? undefined
            : Number(record.whatsapp_fee) || 0
        })
        setNotifyEnabled(enabled)
        return enabled
      }
      return notifyEnabled
    } finally {
      setNotifyFeesLoaded(true)
    }
  }

  // Pré-carrega as tarifas de notificação ao montar (modo edição) para que o
  // modal de confirmação já abra no tamanho final, sem o "pulo" do estado de
  // loading para o conteúdo (o modal usa height:auto e re-centraliza ao mudar
  // de altura).
  useEffect(() => {
    if (isEdit) {
      loadNotifyFees()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [recieverType, setRecieverType] = useState<
    'customer' | 'group' | undefined
  >('customer')
  const [customerUuid, setCustomerUuid] = useState(defaultValues?.customerUuid)
  const [groupUuid, setGroupUuid] = useState(defaultValues?.groupUuid)

  const [productName] = useState<string>('')

  // Handler para erros de validação do formulário
  const handleSubmitError = (errors: any) => {
    // SC-10694: mesmo padrão do `invalid_fields` do cadastro — diz com
    // evidência qual campo impede a criação, em vez de só mostrar um toast.
    track('form_validation_failed', {
      form: isUnique ? 'charge' : 'recurrence',
      invalid_fields: Object.keys(errors || {}),
      is_edit: isEdit,
      is_mobile: isMobileViewport()
    })

    toastFirstError(errors)

    const creditCardError = errors?.payment_type_details?.credit_card?.message

    if (
      creditCardError &&
      creditCardError.includes(
        'Selecione pelo menos uma opção de pagamento para o Cartão de Crédito'
      )
    ) {
      paymentsTypeRef.current?.scrollToCreditCard()
      scrollToCreditCardFnRef.current?.()
    }
  }

  const handleOpenDialog = ({
    title,
    description,
    handleClose,
    action,
    actionLabel
  }: {
    action: () => void
    handleClose: () => void
    title: string
    description: string
    actionLabel: string
  }) => {
    setDialog({
      title,
      description,
      isOpen: true,
      handleClose,
      action,
      actionLabel
    })
  }

  const handleCloseDialog = () => {
    setDialog((s) => ({ ...s, isOpen: false }))
    setIsIssuedDialog(false)
    setWizardStep(1)
  }

  const formatedMessageError = (errorMessage: string) => {
    if (errorMessage?.includes('[')) {
      const firstComma = errorMessage.indexOf('"') + 1
      const lastComma = errorMessage.lastIndexOf('"')
      const message = errorMessage.slice(firstComma, lastComma)

      return message
    }
    return errorMessage
  }

  // Params da ação de nota fiscal no save da cobrança. Lógica pura e testada em
  // `buildNfseActionPayload` (edit.utils) — aqui só passamos o estado do dialog.
  const nfseActionPayload = (): Record<string, unknown> =>
    buildNfseActionPayload({
      isIssuedChargeEdit,
      mode: nfseMode,
      permissions: {
        canCancel: nfsePerms.canCancel,
        canEmit: nfsePerms.canEmit
      },
      confirmedNfseUuid: nfseConfirmed?.uuid ?? null,
      reemit: nfseReemit,
      cancelOld: nfseCancelOld,
      justificativa: nfseJustificativa,
      emitChoice: nfseEmitChoice
    })

  // Formata dados e submete para API
  const handleSubmitForm = async (
    dataForm: ChargeRecurrenceBodyModalForm,
    forceDirtyParam?: boolean
  ) => {
    setLoading(true)

    const workingForm = dataForm

    const loadingMessage = isEdit
      ? `Editando ${isUnique ? 'cobrança' : 'recorrência'}...`
      : `Gerando ${isUnique ? 'cobrança' : 'recorrência'}...`

    const id = toast.loading(loadingMessage)

    const sharedData = formatSharedChargeData({
      dataForm: workingForm,
      isUnique,
      isCharge,
      isEdit,
      editType,
      planDefault
    })

    const notifyChannelsList = [
      ...(notifyChannels.email ? ['email'] : []),
      ...(notifyChannels.whatsapp ? ['whatsapp'] : [])
    ]
    const shouldNotify =
      isIssuedChargeEdit &&
      notifyChoice === 'notify' &&
      notifyChannelsList.length > 0

    const data = {
      ...sharedData,
      ...(forceDirtyParam && { force_dirty: true }),
      ...(customerUuid &&
        recieverType === 'customer' && {
          customers_uuid: customerUuid
        }),
      ...(groupUuid &&
        recieverType === 'group' && {
          customer_groups_uuid: groupUuid
        }),
      ...(shouldNotify && {
        notify_customer: true,
        notify_channels: notifyChannelsList,
        notify_changed_fields: changedSensitiveFields
      }),
      ...nfseActionPayload()
    }

    const apiConfig = {
      data,
      type: isEdit ? ('PATCH' as const) : ('POST' as const),
      url: isEdit
        ? editType === 'charge'
          ? `/v3/billings/${uuid}`
          : `/v3/recurrences/${uuid}`
        : '/v3/billings'
    }

    const response = await DataService(apiConfig)

    const errorMessage =
      response?.data?.detail_error ||
      response?.detail_error ||
      response?.message

    toast.update(id, {
      render: response?.error
        ? formatedMessageError(errorMessage)
        : response?.data?.message,
      type: response?.error ? 'error' : 'success',
      isLoading: false,
      autoClose: 10000,
      closeButton: true
    })

    if (
      (!response?.error && response.status === 201) ||
      (!response?.error && isEdit)
    ) {
      // SC-10694: marco de ativação. Criar a primeira cobrança é o momento em
      // que o merchant passa a usar o produto de verdade — em agosto só 21 de
      // 159 contas novas chegaram aqui. Só criação conta como ativação; edição
      // fica de fora para não inflar o número.
      if (!isEdit) {
        track(isUnique ? 'charge_created' : 'recurrence_created', {
          payment_methods: payments,
          amount_range: amountRange(Number(dataForm?.amountBilled)),
          receiver_type: recieverType,
          total_cycles: dataForm?.totalCycles,
          is_mobile: isMobileViewport()
        })
      }

      const notifyWarnings = response?.data?.notify_warnings
      if (Array.isArray(notifyWarnings) && notifyWarnings.length > 0) {
        notifyWarnings.forEach((w: string) => toast.warn(w))
      }
      // A emissão/reemissão da nota é feita DIRETO no backend via `nfse_action`
      // no payload do save (ver `buildNfseActionPayload`). O resultado volta em
      // `nfse_warnings` acima — não há mais modal de nota pré-preenchido.
      const nfseWarnings = response?.data?.nfse_warnings
      if (Array.isArray(nfseWarnings) && nfseWarnings.length > 0) {
        nfseWarnings.forEach((w: string) => toast.info(w))
      }

      // SC-10999: na visão multi-empresas, o registro criado é da empresa
      // escolhida no formulário. Registrar antes de fechar faz o detalhe
      // aberto logo abaixo buscar com o token dela, e não com o da empresa de
      // contexto.
      const formCompanyUuid = getActiveMerchantUuid()

      onClose(true)

      if (!isEdit) {
        const records = response?.data?.records
        const responseUuid = Array.isArray(records)
          ? records[0]?.uuid
          : records?.uuid

        registerRecordMerchant(responseUuid, formCompanyUuid)

        if (!dataForm.customerUuid?.includes(';')) {
          !!handleOpenDetailChargeOrRecurrence &&
            handleOpenDetailChargeOrRecurrence({
              uuid: responseUuid,
              totalCycles: isUnique ? 1 : dataForm.totalCycles || 0
            })
        }
      }
    }

    if (!errorMessage && !isEdit) {
      reset({
        discountDays: new Date(),
        discountPolicy: false,
        dueDate: new Date(),
        interestPolicy: false,
        totalCycles: isUnique ? 1 : 0
      } as ChargeBodyModalForm)
    }

    handleCloseDialog()
    !!revalidateTable && revalidateTable()
    setLoading(false)
  }

  // Valida formulário e abre dialog de confirmação
  const handleClickToSubmit: SubmitHandler<
    ChargeRecurrenceBodyModalForm
  > = async (dataForm) => {
    // Trigger validation before proceeding
    const isFormValid = await trigger()

    if (!payments?.length) {
      toast.error('Selecione pelo menos um meio de pagamento')
      return
    }

    !!onSubmit && onSubmit(dataForm)
    // totalCycles: 1 = cobrança única (avulsa); 0 = recorrência indeterminada;
    // > 1 = recorrência com N ciclos; 13 = "Outros" (custom). Só a avulsa é !== recorrência.
    const isCorrect = checkDescriptionVariant(
      dataForm.description,
      setError,
      dataForm.totalCycles !== 1
    )
    if (!isCorrect) return
    let hasDiscountErrors = false

    if (dataForm.discountPolicy && dataForm.discounts) {
      dataForm.discounts.forEach((discount, index) => {
        // Verificar erros nos campos de desconto registrados pelo React Hook Form
        const amountError = errors.discounts?.[index]?.amount
        const dateError = errors.discounts?.[index]?.date
        const daysError = errors.discounts?.[index]?.days

        if (amountError || dateError || daysError) {
          hasDiscountErrors = true
        }

        // Verificar campos obrigatórios
        if (!discount.amount || discount.amount.trim() === '') {
          hasDiscountErrors = true
        }
      })
    }

    if (hasDiscountErrors) {
      toast.error('Corrija os erros nos descontos antes de continuar')
      return
    }

    // Validação do identifyInternal agora é feita pelo react-hook-form
    // A validação geral do formulário é controlada pelo isValid do formState

    // Verificar especificamente os switches do cartão de crédito
    const creditCard = dataForm.payment_type_details?.credit_card
    // totalCycles pode ser 0 (valor inicial), 1 (única) ou > 1 (recorrência)
    // Considerar 0 e 1 como cobrança única
    const isRecurrence =
      dataForm.totalCycles !== undefined && dataForm.totalCycles > 1

    // Para recorrências, sempre exigir uma opção selecionada
    if (creditCard?.enabled && isRecurrence) {
      const hasAtLeastOneSwitch = !!(
        creditCard.credit_card_installments.enabled ||
        creditCard.credit_card_recurring.enabled ||
        creditCard.credit_card.enabled
      )

      if (!hasAtLeastOneSwitch) {
        const hasInstallments = !!(
          creditCard?.max_installments && creditCard.max_installments > 1
        )
        const errorMessage = getCreditCardPaymentOptionsText(
          isRecurrence,
          hasInstallments
        )

        toast.error(errorMessage)
        paymentsTypeRef.current?.scrollToCreditCard()
        scrollToCreditCardFnRef.current?.()
        return
      }
    }

    // Para cobranças únicas com parcelas > 1
    if (
      creditCard?.enabled &&
      !isRecurrence &&
      creditCard?.max_installments &&
      creditCard.max_installments > 1
    ) {
      const hasAtLeastOneSwitch = !!(
        creditCard.credit_card_installments?.enabled ||
        creditCard.credit_card_recurring?.enabled ||
        creditCard.credit_card?.enabled
      )

      if (!hasAtLeastOneSwitch) {
        const hasInstallments = true // Já estamos dentro da condição creditCard.installments > 1
        const errorMessage = getCreditCardPaymentOptionsText(
          isRecurrence,
          hasInstallments
        )

        toast.error(errorMessage)
        paymentsTypeRef.current?.scrollToCreditCard()
        scrollToCreditCardFnRef.current?.()
        return
      }
    }

    if (isFormValid) {
      // Sem pergunta de métrica (MRR/AVULSA) na criação nem na edição: ela é
      // decidida no plano ou no item da composição (SC-11024).
      openConfirmationDialog(dataForm)
    }
  }

  // Função para abrir o dialog de confirmação apropriado
  const openConfirmationDialog = (dataForm: ChargeRecurrenceBodyModalForm) => {
    if (isEdit) {
      // Dialog unificado "Confirmar alterações" para qualquer edição (cobrança
      // OU recorrência).

      // Notificação (reenvio) e nota fiscal só se aplicam a COBRANÇA já emitida
      // ao cliente. Recorrência e cobrança pendente: sem reenvio (nunca foi
      // enviada) e sem ação de nota (a policy emite a nota com a config nova
      // quando a cobrança abrir).
      const fields = isIssuedChargeEdit
        ? getChangedSensitiveFields(watch(), editData)
        : []
      setChangedSensitiveFields(fields)

      // Detecção local de defasagem da nota → modo do bloco de opções de nota.
      pendingChargeFormRef.current = dataForm
      const nfseCtx = isIssuedChargeEdit
        ? getNfseEditContext(watch(), editData)
        : null
      const mode = nfseCtx ? resolveNfseDialogMode(nfseCtx) : 'none'
      setNfseMode(mode)
      setNfseConfirmed(nfseCtx?.confirmedNfse ?? null)
      setNfseReemit(nfsePerms.canCancel)
      setNfseCancelOld(false)
      setNfseEmitChoice('now')

      // Reconciliação de cobranças pendentes editadas à mão (só recorrência):
      // default = manter as edições manuais (não sobrescrever).
      setApplyToDirty(false)

      setWizardStep(1)

      setNotifyChoice('silent')
      setNotifyChannels({ email: false, whatsapp: false })
      // Carrega as tarifas de notificação ANTES de abrir o modal, garantindo
      // que ele já nasça no tamanho final (sem o "pulo" loading → conteúdo).
      // Normalmente já estão pré-carregadas no mount, então isto resolve na hora.
      ;(async () => {
        const enabled = await loadNotifyFees()
        // Há algo a decidir no modal de passos? (métrica faltando, notificação
        // de cobrança emitida, defasagem de nota ou reconciliação de pendentes).
        const willNotify =
          isIssuedChargeEdit && !!(enabled?.email || enabled?.whatsapp)
        const willNfse = mode !== 'none'
        const willDirty =
          (isEdit && !isCharge ? editData?.dirtyPendingBillingsCount ?? 0 : 0) >
          0
        const hasAnyStep = willNotify || willNfse || willDirty

        // Nada a decidir → confirmação simples no Dialog padrão da
        // clientbase-library (mesmo do create/edição não emitida), em vez do
        // Modal de passos customizado.
        setIsIssuedDialog(hasAnyStep)
        handleOpenDialog({
          title: 'Confirmar alterações',
          description: hasAnyStep
            ? ''
            : `Confirma salvar as alterações ${
                isCharge ? 'desta cobrança' : 'desta recorrência'
              }?`,
          handleClose: handleCloseDialog,
          action: () => handleSubmitForm(dataForm),
          actionLabel: 'Salvar'
        })
      })()
      return
    } else if (isUnique) {
      // Create charge dialog
      handleOpenDialog({
        title: 'Cobrança',
        description:
          dataForm.recieverType !== 'customer'
            ? `Essa cobrança será criada para${
                groupLength === 1 ? '' : ' as'
              } ${groupLength} pessoa${groupLength === 1 ? '' : 's'} do grupo`
            : `Confirma gerar uma cobrança avulsa para este cliente?`,
        handleClose: handleCloseDialog,
        action: () => handleSubmitForm(dataForm),
        actionLabel: 'Gerar'
      })
    } else {
      // Create recurrence dialog
      let description = ''
      if (dataForm?.isEnablePlan) {
        description = `Confirma criar recorrência associada ao plano ${
          planDefault ? planDefault.name : productName
        }?`
      } else {
        description = !isUnique
          ? dataForm.recieverType !== 'customer'
            ? `Essa cobrança recorrente será criada para ${
                groupLength === 1 ? '' : ' as'
              } ${groupLength} pessoa${groupLength === 1 ? '' : 's'} do grupo`
            : `Confirma gerar uma cobrança recorrente para este cliente?`
          : dataForm.recieverType == 'customer'
          ? `Confirma gerar ${
              dataForm.totalCycles === 13
                ? dataForm?.totalCyclesCustom
                : dataForm?.totalCycles
            } cobranças para este cliente?`
          : `Confirma gerar cobrança para as ${
              dataForm.totalCycles === 13
                ? dataForm?.totalCyclesCustom
                : dataForm?.totalCycles
            } pessoas desse grupo?`
      }

      handleOpenDialog({
        title: dataForm.totalCycles !== 1 ? 'Recorrência' : 'Cobrança',
        description,
        handleClose: handleCloseDialog,
        action: () => handleSubmitForm(dataForm),
        actionLabel: 'Gerar'
      })
    }
  }

  // Initialize form with edit data if in edit mode
  useEffect(() => {
    if (isEdit && editData) {
      // Set initial mode based on edit type
      setIsCharge(editType === 'charge')
      setIsUnique(editType === 'charge')

      // Map data to form values based on edit type
      if (editType === 'charge') {
        const formValues = mapChargeToFormValues(editData)

        // Set form-specific values (não compartilhados)
        setValue('amountBilled', formValues.amountBilled as any)
        setValue('dueDate', formValues.dueDate as any)
        setValue(
          'hasItemsOnDescription',
          formValues.hasItemsOnDescription as any
        )
        setValue('items', formValues.items as any)
        setValue('description', formValues.description as any)
        setValue('expirationField', formValues.expirationField as any)
        setValue('hasIssueDays', formValues.hasIssueDays as any)
        setValue('issueDays', formValues.issueDays as any)
        if (formValues.issueBillingType) {
          setValue('issueBillingType', formValues.issueBillingType as any)
        }

        // Set customer data
        if (editData.customer) {
          setCustomerUuid(editData.customer.uuid)
          setValue('customer', {
            label: editData.customer.name,
            value: editData.customer.uuid
          })
        }

        // Usar função utilitária para dados compartilhados
        // Não passamos items aqui pois já foram setados acima com mapChargeToFormValues
        // (que inclui o item de Multa/Juros para faturas expiradas)
        setSharedFormData(
          {
            ...editData,
            nfsesScheduled: editData.nfsesScheduled,
            nfsePolicy: editData.nfsePolicy,
            items: undefined // Não sobrescrever items já processados
          },
          setValue,
          undefined,
          false,
          undefined,
          false // isProductOrLink
        )
      } else {
        // Handle recurrence edit
        const formValues = mapRecurrenceToFormValues(editData)

        // Set form-specific values (não compartilhados)
        setValue('description', formValues.description as any)
        setValue('hasIssueDays', formValues.hasIssueDays as any)
        setValue('issueDays', formValues.issueDays as any)
        if (formValues.issueBillingType) {
          setValue('issueBillingType', formValues.issueBillingType as any)
        }

        // Set dueDay explicitly for recurrence edit
        if (formValues.dueDay !== undefined) {
          setValue('dueDay', Number(formValues.dueDay) as any)
        }

        // Set customer data
        if (editData.customer) {
          setCustomerUuid(editData.customer.uuid)
          setValue('customer', {
            label: editData.customer.name,
            value: editData.customer.uuid
          })
        }

        // Usar função utilitária para dados compartilhados
        setSharedFormData(
          {
            ...editData,
            nfsesScheduled: editData.nfsesScheduled,
            nfsePolicy: editData.nfsePolicy,
            items: editData.items
          },
          setValue,
          undefined,
          true,
          undefined,
          false // isProductOrLink
        )
      }

      // Trigger validation after setting form values to show errors if form is invalid
      setTimeout(() => {
        trigger().then((isFormValid) => {
          // Only trigger if form is invalid to show validation errors
          if (!isFormValid) {
            trigger()
          }
        })
      }, 100)
    }
  }, [isEdit, editData, editType, setValue, trigger])

  // Preserva meios de pagamento no modo de edição (com normalização para recorrência)
  useEffect(() => {
    if (isEdit && editData) {
      const originalPayments = editData.paymentType.split(';').filter(Boolean)

      // Se houver qualquer variante de cartão, incluir 'credit_card' para refletir o card visual
      let normalizedOriginal = originalPayments
      {
        const hasCC = originalPayments.includes('credit_card')
        const hasCCRecurring = originalPayments.includes(
          'credit_card_recurring'
        )
        const hasCCInstallments = originalPayments.includes(
          'credit_card_installments'
        )
        if (!hasCC && (hasCCRecurring || hasCCInstallments)) {
          normalizedOriginal = [...originalPayments, 'credit_card']
        }
      }

      const currentPayments = watch('payments') || []

      // Comparação por conjunto (ordem independente)
      const toKey = (arr: string[]) => arr.slice().sort().join(';')
      const isDifferent = toKey(currentPayments) !== toKey(normalizedOriginal)

      if (isDifferent) {
        setValue('payments', normalizedOriginal)
      }
    }
  }, [watch('items'), isEdit, editData, editType])

  // useEffect para ajustar automaticamente amount_type quando issue_when muda
  useEffect(() => {
    const currentValues = watch()

    if (
      currentValues.interestInvoice &&
      currentValues.issue_when &&
      currentValues.amount_type
    ) {
      const isBlockedIssueWhen = [
        'on_custom_issued',
        'on_billing_issued'
      ].includes(currentValues.issue_when)
      const isAmountPaid = currentValues.amount_type === 'amount_paid'

      // Se a combinação não é permitida, muda automaticamente para 'amount_issued'
      if (isBlockedIssueWhen && isAmountPaid) {
        setValue('amount_type', 'amount_issued')

        // Limpa o campo amountCustom se estava preenchido
        if (currentValues.amountCustom) {
          setValue('amountCustom', '')
        }

        // Força revalidação
        trigger('amount_type')
      }
    }
  }, [
    watch('issue_when'),
    watch('amount_type'),
    watch('interestInvoice'),
    setValue,
    trigger
  ])

  const submitButtonElement = (
    <Button
      onClick={handleSubmit(handleClickToSubmit, handleSubmitError)}
      type="button"
      variantButton="primary"
    >
      {isEdit ? 'Salvar' : 'Gerar'} {isUnique ? 'cobrança' : 'recorrência'}
    </Button>
  )
  // SC-10999: na visão multi-empresas o bloqueio de criação é o da empresa
  // escolhida no formulário (o botão que abre o modal não sabe qual é).
  const submitButton =
    inCompanyForm && !isEdit ? (
      <CreateGate resourceName={isUnique ? 'Cobranças' : 'Recorrências'}>
        {submitButtonElement}
      </CreateGate>
    ) : (
      submitButtonElement
    )

  return (
    <React.Fragment>
      <ModalBody
        display="flex"
        flexDirection="column"
        component="form"
        onSubmit={(e) => e.preventDefault()}
      >
        <ModalHeader onClose={() => onClose(false)}>
          <Stack direction="row" alignItems="center" gap={2}>
            <IconContainer icon={isUnique ? 'requestQuote' : 'eventRepeat'} />
            <Typography variant="mdBold">
              {isEdit
                ? `Editar ${isUnique ? 'Cobrança' : 'Recorrência'}`
                : `Nova ${isUnique ? 'Cobrança' : 'Recorrência'}`}
            </Typography>
          </Stack>
        </ModalHeader>

        <ModalContent>
          {companySelector}

          <PayersAutocomplete
            setGroupLength={setGroupLength}
            control={control}
            nameCustomer={nameCustomer || editData?.customer?.name}
            setCustomerUuid={setCustomerUuid}
            setGroupUuid={setGroupUuid}
            recieverType={recieverType}
            setRecieverType={setRecieverType}
            watch={watch}
            setValue={setValue}
            edit={isEdit}
            isUnique={isUnique}
          />

          <SharedChargeFields
            control={control}
            watch={watch}
            setValue={setValue}
            trigger={trigger}
            errors={errors}
            register={register}
            clearErrors={clearErrors}
            isCharge={isCharge}
            setIsCharge={setIsCharge}
            isUnique={isUnique}
            setIsUnique={setIsUnique}
            isEdit={isEdit}
            isDuplicate={isDuplicate}
            editData={editData}
            customerUuid={customerUuid}
            planDefault={planDefault}
            editType={editType}
            hiddenSections={[]}
            isProductOrLink={false}
            paymentsTypeRef={paymentsTypeRef}
            onScrollToCreditCardReady={(fn) => {
              scrollToCreditCardFnRef.current = fn
            }}
          />
        </ModalContent>

        <ModalFooter>
          <Stack direction="row" alignItems="center" gap={2}>
            {/* Ícone de Tags */}
            <TagsPopover control={control} name="tags" maxTags={10} />

            {submitButton}
          </Stack>
        </ModalFooter>
      </ModalBody>

      {/* Confirmação simples (criar / editar cobrança não emitida) */}
      <Dialog
        icon={isCharge ? 'requestQuote' : 'eventRepeat'}
        title={dialog.title}
        description={dialog.description}
        setIsOpenDialog={dialog.handleClose}
        isOpenDialog={dialog.isOpen && !isIssuedDialog}
        cancelButton
      >
        <Button
          id="confirm-generate-charge"
          autoFocus
          disabled={loading}
          loading={loading}
          onClick={dialog.action}
          fullWidth
        >
          {dialog.actionLabel}
        </Button>
      </Dialog>

      {/* Confirmação em passos da edição de cobrança já emitida (Variante 8) */}
      <Modal
        open={dialog.isOpen && isIssuedDialog}
        handleClose={() => dialog.handleClose()}
        size="small"
        width={560}
        height="auto"
        maxHeight="90vh"
        zIndex={1400}
        disablePortal={false}
      >
        <ModalBody>
          <ModalHeader onClose={() => dialog.handleClose()}>
            <Stack direction="row" alignItems="center" gap={1.25}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '10px',
                  backgroundColor: '#F2F5F2',
                  display: 'grid',
                  placeItems: 'center',
                  flex: 'none'
                }}
              >
                <Icon
                  icon={isCharge ? 'requestQuote' : 'eventRepeat'}
                  size={3}
                  colorSX="#646664"
                />
              </Box>
              <Typography variant="mdBold">Confirmar alterações</Typography>
            </Stack>
          </ModalHeader>

          <ModalContent>
            {!notifyFeesLoaded ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 220,
                  py: 4
                }}
              >
                <CircularProgress size={28} sx={{ color: '#3F403F' }} />
              </Box>
            ) : (
              <Stack direction="column" spacing={1.5} width="100%">
                {isWizard && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ pb: 4 }}
                  >
                    {[
                      { n: 1 as const, label: 'Cobrança' },
                      { n: 2 as const, label: 'Nota fiscal' }
                    ].map((s, i) => (
                      <React.Fragment key={s.n}>
                        {i > 0 && (
                          <Box
                            sx={{
                              flex: 1,
                              height: '2px',
                              backgroundColor:
                                wizardStep > 1 ? '#01F7C2' : '#E3E6E3'
                            }}
                          />
                        )}
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.75}
                        >
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              display: 'grid',
                              placeItems: 'center',
                              fontSize: 12,
                              fontWeight: 700,
                              backgroundColor:
                                wizardStep === s.n
                                  ? '#3F403F'
                                  : wizardStep > s.n
                                  ? '#01F7C2'
                                  : '#E3E6E3',
                              color:
                                wizardStep === s.n
                                  ? '#fff'
                                  : wizardStep > s.n
                                  ? '#3F403F'
                                  : '#777877'
                            }}
                          >
                            {wizardStep > s.n ? '✓' : s.n}
                          </Box>
                          <Typography
                            variant="smBold"
                            sx={{
                              color: wizardStep === s.n ? '#3F403F' : '#777877'
                            }}
                          >
                            {s.label}
                          </Typography>
                        </Stack>
                      </React.Fragment>
                    ))}
                  </Stack>
                )}

                {onStep1 && (
                  <Stack direction="column" spacing={1.5} width="100%">
                    {showNotifySection && (
                      <NotifyCustomerFields
                        changedFields={changedSensitiveFields}
                        notifyChoice={notifyChoice}
                        onNotifyChoiceChange={setNotifyChoice}
                        channels={notifyChannels}
                        onChannelsChange={setNotifyChannels}
                        fees={notifyFees}
                        enabled={notifyEnabled}
                      />
                    )}
                    {showNotifySection && notifyChoice === 'notify' && (
                      <Box sx={{ minHeight: 20, textAlign: 'center' }}>
                        {!notifyChannels.email && !notifyChannels.whatsapp && (
                          <Typography variant="smLight" color="text.secondary">
                            Selecione ao menos um canal
                          </Typography>
                        )}
                      </Box>
                    )}

                    {showDirtyChoice && (
                      <React.Fragment>
                        {showNotifySection && <Divider />}
                        <Stack direction="column" spacing={1}>
                          <Typography variant="smBold">
                            Cobranças editadas manualmente
                          </Typography>
                          <Typography variant="smLight" color="text.secondary">
                            {dirtyPendingCount === 1
                              ? '1 cobrança pendente desta recorrência foi editada manualmente.'
                              : `${dirtyPendingCount} cobranças pendentes desta recorrência foram editadas manualmente.`}{' '}
                            Como aplicar as alterações?
                          </Typography>
                          <RadioGroup
                            value={applyToDirty ? 'apply' : 'keep'}
                            onChange={(_, v) => setApplyToDirty(v === 'apply')}
                          >
                            <FormControlLabel
                              value="keep"
                              control={<Radio size="small" />}
                              label={
                                <Typography variant="smLight">
                                  Manter as edições manuais (não sobrescrever)
                                </Typography>
                              }
                            />
                            <FormControlLabel
                              value="apply"
                              control={<Radio size="small" />}
                              label={
                                <Typography variant="smLight">
                                  Aplicar as alterações da recorrência nessas
                                  cobranças também
                                </Typography>
                              }
                            />
                          </RadioGroup>
                        </Stack>
                      </React.Fragment>
                    )}
                  </Stack>
                )}

                {onStep2 && (
                  <NfseReissueFields
                    mode={nfseMode}
                    confirmedNfseNumber={nfseConfirmed?.nfse_number}
                    permissions={nfsePerms}
                    reemit={nfseReemit}
                    onReemitChange={setNfseReemit}
                    cancelOld={nfseCancelOld}
                    onCancelOldChange={setNfseCancelOld}
                    justificativa={nfseJustificativa}
                    onJustificativaChange={setNfseJustificativa}
                    emitChoice={nfseEmitChoice}
                    onEmitChoiceChange={setNfseEmitChoice}
                  />
                )}

                {/* Safety net: este Modal de passos só é aberto quando há algo
                  a decidir (ver `openConfirmationDialog`). Quando não há, a
                  confirmação simples usa o <Dialog> da clientbase-library, não
                  este bloco. Mantido como defesa caso um step suma em runtime. */}
                {!onStep1 && !onStep2 && (
                  <Typography variant="smLight" color="text.secondary">
                    Confirma salvar as alterações{' '}
                    {isCharge ? 'desta cobrança' : 'desta recorrência'}?
                  </Typography>
                )}
              </Stack>
            )}
          </ModalContent>

          <ModalFooter>
            {notifyFeesLoaded && isWizard && (
              <Typography
                variant="smLight"
                color="text.secondary"
                sx={{ mr: 'auto', alignSelf: 'center' }}
              >
                Passo {wizardStep} de 2
              </Typography>
            )}
            <Button
              variantButton="neutral"
              onClick={() => dialog.handleClose()}
            >
              Cancelar
            </Button>
            {isWizard && wizardStep === 2 && (
              <Button variantButton="ghost" onClick={() => setWizardStep(1)}>
                Voltar
              </Button>
            )}
            {isWizard && wizardStep === 1 ? (
              <Button
                id="confirm-generate-charge"
                autoFocus
                disabled={
                  !notifyFeesLoaded ||
                  (notifyChoice === 'notify' &&
                    !notifyChannels.email &&
                    !notifyChannels.whatsapp)
                }
                onClick={() => setWizardStep(2)}
              >
                Próximo
              </Button>
            ) : (
              <Button
                id="confirm-generate-charge"
                autoFocus
                disabled={
                  !notifyFeesLoaded ||
                  loading ||
                  (notifyChoice === 'notify' &&
                    !notifyChannels.email &&
                    !notifyChannels.whatsapp)
                }
                loading={loading}
                onClick={() => {
                  if (pendingChargeFormRef.current) {
                    handleSubmitForm(pendingChargeFormRef.current, applyToDirty)
                  }
                }}
              >
                {loading
                  ? 'Salvando...'
                  : notifyChoice === 'notify'
                  ? 'Salvar e notificar novamente'
                  : 'Salvar'}
              </Button>
            )}
          </ModalFooter>
        </ModalBody>
      </Modal>
    </React.Fragment>
  )
}

/**
 * SC-10999: na visão multi-empresas a criação escolhe a empresa no topo do
 * formulário. Quase tudo aqui depende da empresa (clientes, grupos, planos,
 * tags, campos extras, cupons, emissor de nota e os padrões de juros e
 * emissão), então trocar a empresa remonta o formulário inteiro: as seleções
 * da empresa anterior somem e os autocompletes buscam de novo, já com o token
 * da nova. Fora da visão do grupo, e em edição/duplicação, é o formulário de
 * sempre.
 */
function BodyModalNewChargeOrRecurrence(props: ChargeBodyModalClientProps) {
  const { companyUuid, setCompanyUuid } = useFormCompany({
    enabled: !props.isEdit && !props.isDuplicate
  })

  return (
    <CompanySessionProvider companyUuid={companyUuid}>
      <BodyModalNewChargeOrRecurrenceForm
        key={companyUuid || 'context'}
        {...props}
        companySelector={
          <CompanySelector value={companyUuid} onChange={setCompanyUuid} />
        }
      />
    </CompanySessionProvider>
  )
}

export default BodyModalNewChargeOrRecurrence
